import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { getCanonicalUrl, getAlternateUrls } from "../../Utility/seoUtils";

// Import critical font assets so Webpack resolves their hashed production paths
import rubikRegular from "../../Assets/fonts/rubik/RubikRegular.woff2";
import rubikMedium from "../../Assets/fonts/rubik/Rubik-Medium.woff2";
import coHeadlineLight from "../../Assets/fonts/coheadline/CoHeadlineLight.woff";
import coHeadlineRegular from "../../Assets/fonts/coheadline/CoHeadline.woff";

// Site-wide default og:image / twitter:image. Every page inherits this
// unless it passes its own `image` prop. Fixes the "0/44 og:image" gap the
// SEO audit flagged after ~2 minutes of edit + a rebuild, instead of
// touching 44 individual pages.
const DEFAULT_OG_IMAGE =
  "https://storage.tameen.om/tameen/newtm/tameen_newlogo.webp";

const SeoHelmet = ({
  title,
  description,
  keywords,
  image = DEFAULT_OG_IMAGE,
  twitterCard = "summary_large_image",
  ogType = "website",
  noIndex = false,
  is404 = false,
  lang = "en",
  schema,
}) => {
  const is404Page = is404;
  const canonical = is404Page ? null : getCanonicalUrl(lang);
  const alternates = is404Page ? null : getAlternateUrls();
  const en = alternates?.en;
  const ar = alternates?.ar;

  const isStaging =
    typeof window !== "undefined" &&
    (window.location.hostname === "staging.tameen.om" ||
      window.location.hostname === "mis.tameen.om" ||
      window.location.hostname === "dev.tameen.om");
  const shouldNoIndex = noIndex || is404Page || isStaging;

  // Defer Contentsquare tracking to idle after page load on client only.
  // Never run or commit to DOM during prerendering (window.__PRERENDERING__).
  useEffect(() => {
    if (typeof window === "undefined" || window.__PRERENDERING__) return;
    if (window.__cs_loaded__) return;

    const loadCs = () => {
      if (window.__cs_loaded__) return;
      window.__cs_loaded__ = true;
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = "https://t.contentsquare.net/uxa/03b1b833c6f57.js";
      script.async = true;
      document.head.appendChild(script);
    };

    if (document.readyState === "complete") {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(loadCs, { timeout: 3000 });
      } else {
        setTimeout(loadCs, 2500);
      }
    } else {
      window.addEventListener(
        "load",
        () => {
          if ("requestIdleCallback" in window) {
            window.requestIdleCallback(loadCs, { timeout: 3000 });
          } else {
            setTimeout(loadCs, 2500);
          }
        },
        { passive: true, once: true }
      );
    }
  }, []);

  return (
    <>
      <Helmet>
        {/* HTML LANG */}
        <html lang={lang} />

        {/* BASIC SEO */}
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}

        {/* CANONICAL */}
        {canonical && <link rel="canonical" href={canonical} />}

        {/* HREFLANG */}
        {en && <link rel="alternate" hrefLang="en" href={en} />}
        {ar && <link rel="alternate" hrefLang="ar" href={ar} />}
        {en && <link rel="alternate" hrefLang="x-default" href={en} />}

        {/* CRITICAL FONT PRELOADS (Above the fold: body font, navigation font, and primary heading font) */}
        <link
          rel="preload"
          href={rubikRegular}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={rubikMedium}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href={lang === "ar" ? coHeadlineRegular : coHeadlineLight}
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />

        {/* OPEN GRAPH (FACEBOOK / LINKEDIN) */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:type" content={ogType} />
        {canonical && <meta property="og:url" content={canonical} />}
        <meta property="og:site_name" content="Tameen" />

        {/* TWITTER */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />

        {/* NOINDEX */}
        {shouldNoIndex && <meta name="robots" content="noindex,nofollow" />}
      </Helmet>

      {/* ✅ JSON-LD SCHEMA RENDERED IN DOM FOR PRERENDERING SUPPORT */}
      {schema &&
        (Array.isArray(schema) ? schema : [schema]).map((item, index) => (
          <script
            key={index}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
          />
        ))}
    </>
  );
};

export default SeoHelmet;